import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil, isString } from 'lodash';
// Import Helpers
import { genericMemo } from 'services/Helpers';
// Import Components
import KeyValueItemBase, { type KeyValueItemBaseProps } from 'components/common/KeyValueItemBase';
// Import Constants
import { l } from 'constants/common';

const DEFAULT_CLASSNAMES = {
    pair: 'key-value-info-content',
    key: 'key-value-items-title',
    value: 'key-value-items-subtitle',
    skeleton: 'key-value-items-skeleton',
};

export interface KeyValueItemProps<T extends boolean = false> extends Omit<KeyValueItemBaseProps<T>, 'classNames'> {
    vertical?: boolean;
    classNames?: KeyValueItemBaseProps<T>['classNames'];
}

const KeyValueItem = <T extends boolean = false>(props: KeyValueItemProps<T>) => {
    const { isLoading, label, value, classNames = DEFAULT_CLASSNAMES, vertical = false, ...rest } = props;
    const { t } = useTranslation();
    const [classNamesBase, setClassNamesBase] = useState({ ...classNames });

    useEffect(() => {
        setClassNamesBase(() => {
            const result = { ...classNames };
            let additionalClassName = 'key-value-info-content';
            if (vertical) {
                additionalClassName = 'key-value-items-vertical';
                result.pair = `${classNamesBase.pair} ${additionalClassName}`;
            }
            return result;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classNames, vertical]);

    return (
        <KeyValueItemBase
            label={label}
            value={isNil(value) || (isString(value) && value.trim().length === 0) ? t(l.NA) : value}
            isLoading={isLoading}
            classNames={classNamesBase}
            {...rest}
        />
    );
};

export default genericMemo(KeyValueItem);
